import React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
    visible: boolean;
    onToggle: () => void;
    showLabel: string;
    hideLabel: string;
    className?: string;
    playwrightId?: string;
};

/**
 * Eye / eye-off button used to reveal or mask a password field. Presentational only: the parent owns
 * the visibility state and passes already-localized `showLabel` / `hideLabel` strings. Positioned
 * absolutely over the right edge of its (relative) input container.
 */
export function PasswordRevealToggle({
    visible,
    onToggle,
    showLabel,
    hideLabel,
    className,
    playwrightId,
}: Props) {
    const label = visible ? hideLabel : showLabel;
    const Icon = visible ? EyeOff : Eye;

    return (
        <button
            type="button"
            // Keep focus on the password field when toggling so typing is not interrupted.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onToggle}
            aria-label={label}
            aria-pressed={visible}
            title={label}
            data-playwright-id={playwrightId}
            // `ACCOUNT-ACCESS-5` / `SETTINGS-3`: p-1 around a size-4 glyph is exactly 24x24 CSS px,
            // against the 44px coarse-pointer floor. The hit EXPANDER rather than a bigger box,
            // because this control sits inside a 36-48px field: growing the painted eye would crowd
            // the field it overlays and move the glyph off its anchor. The utility sets no position
            // by design and this element is already absolute, so it supplies its own containing
            // block. Guarded by tapTargets.contract.test.tsx.
            className={cn(
                "absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-md p-1 touch-hit-44 text-muted-foreground transition-colors hover:text-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                className,
            )}
        >
            <Icon className="size-4" aria-hidden="true" />
        </button>
    );
}

export default PasswordRevealToggle;
